// src/filterable-collection-list/filtering.ts
function normalizeFilterValue(value) {
  if (value === undefined)
    return "";
  return value.trim().toLowerCase();
}
function readProductTags(raw) {
  if (raw === undefined || raw.trim() === "") {
    throw new Error("Missing data-product-tags. Expected a JSON array of strings.");
  }
  const parsed = JSON.parse(raw);
  if (!Array.isArray(parsed)) {
    throw new Error("data-product-tags must contain a JSON array.");
  }
  const tags = new Set;
  for (const value of parsed) {
    if (typeof value !== "string") {
      throw new Error("Every data-product-tags entry must be a string.");
    }
    const tag = normalizeFilterValue(value);
    if (tag !== "")
      tags.add(tag);
  }
  return tags;
}
function getTagCandidates(group, value) {
  const normalizedGroup = normalizeFilterValue(group);
  const normalizedValue = normalizeFilterValue(value);
  const candidates = new Set([normalizedValue]);
  if (normalizedGroup === "" || normalizedValue === "")
    return candidates;
  let baseValue = normalizedValue;
  for (const separator of [":", "-", "_"]) {
    const prefix = `${normalizedGroup}${separator}`;
    if (normalizedValue.startsWith(prefix)) {
      baseValue = normalizedValue.slice(prefix.length);
      break;
    }
  }
  if (baseValue !== "") {
    candidates.add(baseValue);
    for (const separator of [":", "-", "_"]) {
      candidates.add(`${normalizedGroup}${separator}${baseValue}`);
    }
  }
  return candidates;
}
function matchesFilters(tags, filters) {
  for (const [group, value] of filters) {
    let groupMatches = false;
    for (const candidate of getTagCandidates(group, value)) {
      if (tags.has(candidate)) {
        groupMatches = true;
        break;
      }
    }
    if (!groupMatches)
      return false;
  }
  return true;
}

// src/filterable-collection-list/filter-collection.ts
var ROOT_SELECTOR = '[id^="LuxuryProductList-"]';
var CONTROL_SELECTOR = "[data-product-filter], [data-clear-filters]";
var controllers = new WeakMap;

class LuxuryProductList {
  root;
  cardElements;
  filterElements;
  clearButton;
  grid;
  countElement;
  emptyState;
  events = new AbortController;
  activeFilters = new Map;
  animationFrames = new Map;
  originalDisabled = new Map;
  clearWasDisabled;
  cards = [];
  controls = [];
  state = "new";
  navigationMode = "none";
  constructor(root) {
    this.root = root;
    this.cardElements = Array.from(root.querySelectorAll("[data-product-card]"));
    this.filterElements = Array.from(root.querySelectorAll("[data-product-filter]"));
    this.clearButton = root.querySelector("[data-clear-filters]");
    this.grid = root.querySelector("[data-product-grid]");
    this.countElement = root.querySelector("[data-result-count]");
    this.emptyState = root.querySelector("[data-empty-state]");
    for (const element of this.filterElements) {
      if (element instanceof HTMLButtonElement) {
        this.originalDisabled.set(element, element.disabled || element.getAttribute("aria-disabled") === "true");
      }
    }
    this.clearWasDisabled = this.clearButton?.disabled === true;
  }
  start() {
    if (this.state !== "new")
      return;
    this.runSafely("initialize", () => {
      delete this.root.dataset.filterError;
      this.validateMarkup();
      this.readConfiguration();
      this.cards = this.cardElements.map((element, index) => {
        try {
          return { element, tags: readProductTags(element.dataset.productTags) };
        } catch (error) {
          console.error(this.logPrefix(), `Invalid tags on card ${index + 1}.`, element, error);
          throw new Error(`Invalid product tags on card ${index + 1}.`);
        }
      });
      this.controls = this.filterElements.map((element) => this.readControl(element));
      this.root.addEventListener("click", this.handleClick, { signal: this.events.signal });
      this.render();
      this.root.dataset.initialized = "true";
      this.debug("Initialized.", { cards: this.cards.length, filters: this.controls.length });
    });
  }
  destroy() {
    if (this.state === "destroyed")
      return;
    this.events.abort();
    this.cancelAnimations();
    this.activeFilters.clear();
    for (const [button, disabled] of this.originalDisabled) {
      button.disabled = disabled;
      button.setAttribute("aria-disabled", String(disabled));
      button.setAttribute("aria-pressed", "false");
      button.classList.remove("is-active");
    }
    if (this.clearButton !== null) {
      this.clearButton.disabled = this.clearWasDisabled;
      this.clearButton.hidden = true;
    }
    for (const card of this.cardElements) {
      card.hidden = false;
      card.setAttribute("aria-hidden", "false");
    }
    this.updateResultSummary(this.cardElements.length);
    this.root.dataset.activeFilters = "[]";
    this.setState("destroyed");
    delete this.root.dataset.initialized;
    this.debug("Destroyed; listeners and animations removed.");
  }
  validateMarkup() {
    for (const element of this.cardElements) {
      if (!(element instanceof HTMLElement)) {
        throw new Error("Every product card must be an HTML element.");
      }
    }
    for (const element of this.filterElements) {
      if (!(element instanceof HTMLButtonElement)) {
        throw new Error("Every data-product-filter control must be a <button>.");
      }
    }
    if (this.clearButton !== null && !(this.clearButton instanceof HTMLButtonElement)) {
      throw new Error("The data-clear-filters control must be a <button>.");
    }
  }
  readConfiguration() {
    const value = this.root.dataset.afterFilter;
    if (value === undefined || value === "" || value === "none")
      return;
    if (value === "grid" || value === "first-match") {
      this.navigationMode = value;
      return;
    }
    console.warn(this.logPrefix(), "Unrecognized data-after-filter; scrolling disabled.", value);
  }
  readControl(element) {
    const group = normalizeFilterValue(element.dataset.filterGroup);
    const tag = normalizeFilterValue(element.dataset.filterTag);
    let lockedByMarkup = this.originalDisabled.get(element) === true;
    if (group === "" || tag === "") {
      lockedByMarkup = true;
      console.warn(this.logPrefix(), "Filter locked: missing group or tag.", element);
    }
    element.type = "button";
    return { element, group, tag, lockedByMarkup };
  }
  handleClick = (event) => {
    if (!(event.target instanceof Element))
      return;
    const button = event.target.closest(CONTROL_SELECTOR);
    if (!(button instanceof HTMLButtonElement))
      return;
    if (button.closest(ROOT_SELECTOR) !== this.root)
      return;
    event.preventDefault();
    if (this.state !== "ready")
      return;
    if (!this.root.isConnected)
      return;
    if (button.disabled || button.getAttribute("aria-disabled") === "true")
      return;
    this.runSafely("filter-click", () => {
      if (button === this.clearButton) {
        if (this.activeFilters.size === 0)
          return;
        this.activeFilters.clear();
      } else {
        const control = this.controls.find((item) => item.element === button);
        if (control === undefined || control.lockedByMarkup)
          return;
        if (!this.selectFilter(control))
          return;
      }
      this.render();
      this.scrollAfterFilter();
    });
  };
  selectFilter(control) {
    const isActive = this.activeFilters.get(control.group) === control.tag;
    if (isActive) {
      if (this.root.dataset.allowFilterDeselect !== "true")
        return false;
      this.activeFilters.delete(control.group);
      return true;
    }
    const candidateFilters = new Map(this.activeFilters);
    candidateFilters.set(control.group, control.tag);
    if (this.root.dataset.disableImpossibleFilters === "true") {
      if (this.getMatchingCards(candidateFilters).length === 0)
        return false;
    }
    this.activeFilters.set(control.group, control.tag);
    return true;
  }
  getMatchingCards(filters = this.activeFilters) {
    return this.cards.filter((card) => matchesFilters(card.tags, filters));
  }
  render() {
    this.setState("updating");
    const matches = this.getMatchingCards();
    const matchingCards = new Set(matches);
    for (const card of this.cards) {
      const shouldShow = matchingCards.has(card);
      const wasHidden = card.element.hidden;
      card.element.hidden = !shouldShow;
      card.element.setAttribute("aria-hidden", String(!shouldShow));
      if (!shouldShow)
        this.cancelAnimation(card.element);
      if (shouldShow && wasHidden)
        this.revealCard(card.element);
    }
    this.updateControls();
    this.updateResultSummary(matches.length);
    this.root.dataset.activeFilters = JSON.stringify(Array.from(this.activeFilters));
    this.setState("ready");
    this.debug("Filters applied.", { count: matches.length, filters: Array.from(this.activeFilters) });
  }
  updateControls() {
    for (const control of this.controls) {
      const isActive = this.activeFilters.get(control.group) === control.tag;
      let disabled = control.lockedByMarkup;
      if (!disabled && !isActive && this.root.dataset.disableImpossibleFilters === "true") {
        const candidateFilters = new Map(this.activeFilters);
        candidateFilters.set(control.group, control.tag);
        disabled = this.getMatchingCards(candidateFilters).length === 0;
      }
      control.element.classList.toggle("is-active", isActive);
      control.element.setAttribute("aria-pressed", String(isActive));
      control.element.disabled = disabled;
      control.element.setAttribute("aria-disabled", String(disabled));
    }
    if (this.clearButton !== null) {
      this.clearButton.type = "button";
      this.clearButton.hidden = this.activeFilters.size === 0;
    }
  }
  updateResultSummary(count) {
    if (this.countElement !== null)
      this.countElement.textContent = String(count);
    if (this.emptyState !== null)
      this.emptyState.hidden = count > 0;
  }
  revealCard(card) {
    this.cancelAnimation(card);
    card.classList.add("is-entering");
    const firstFrame = requestAnimationFrame(() => {
      const secondFrame = requestAnimationFrame(() => {
        card.classList.remove("is-entering");
        this.animationFrames.delete(card);
      });
      this.animationFrames.set(card, secondFrame);
    });
    this.animationFrames.set(card, firstFrame);
  }
  cancelAnimation(card) {
    const frame = this.animationFrames.get(card);
    if (frame !== undefined)
      cancelAnimationFrame(frame);
    this.animationFrames.delete(card);
    card.classList.remove("is-entering");
  }
  cancelAnimations() {
    for (const card of this.animationFrames.keys())
      this.cancelAnimation(card);
  }
  scrollAfterFilter() {
    if (this.navigationMode === "none")
      return;
    let target = this.grid;
    if (this.navigationMode === "first-match") {
      const firstMatch = this.cards.find((card) => !card.element.hidden);
      if (firstMatch !== undefined)
        target = firstMatch.element;
    }
    if (target === null)
      return;
    try {
      let behavior = "auto";
      if (this.root.dataset.smoothScroll === "true")
        behavior = "smooth";
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches)
        behavior = "auto";
      target.scrollIntoView({ behavior, block: "start" });
    } catch (error) {
      console.warn(this.logPrefix(), "Scrolling failed; filters remain usable.", error);
    }
  }
  runSafely(operation, action) {
    try {
      action();
    } catch (error) {
      console.error(this.logPrefix(), `Failed during ${operation}. Showing all products.`, error);
      this.fail(operation);
    }
  }
  fail(operation) {
    this.setState("failed");
    this.events.abort();
    this.cancelAnimations();
    this.activeFilters.clear();
    this.root.dataset.filterError = operation;
    this.root.dataset.activeFilters = "[]";
    for (const card of this.cardElements) {
      card.hidden = false;
      card.setAttribute("aria-hidden", "false");
      card.classList.remove("is-entering");
    }
    for (const button of this.filterElements) {
      button.disabled = true;
      button.setAttribute("aria-disabled", "true");
      button.setAttribute("aria-pressed", "false");
      button.classList.remove("is-active");
    }
    if (this.clearButton !== null) {
      this.clearButton.hidden = true;
      this.clearButton.disabled = true;
    }
    this.updateResultSummary(this.cardElements.length);
  }
  setState(state) {
    this.state = state;
    this.root.dataset.filterState = state;
  }
  logPrefix() {
    return `[LuxuryProductList:${this.root.id}]`;
  }
  debug(message, details = {}) {
    if (this.root.dataset.filterDebug !== "true")
      return;
    console.info(this.logPrefix(), message, details);
  }
}
function findRoots(scope) {
  const roots = [];
  if (scope instanceof HTMLElement && scope.matches(ROOT_SELECTOR))
    roots.push(scope);
  for (const element of scope.querySelectorAll(ROOT_SELECTOR)) {
    if (element instanceof HTMLElement)
      roots.push(element);
  }
  return roots;
}
function initializeWithin(scope) {
  for (const root of findRoots(scope)) {
    if (controllers.has(root))
      continue;
    const controller = new LuxuryProductList(root);
    controllers.set(root, controller);
    controller.start();
  }
}
function destroyWithin(scope) {
  for (const root of findRoots(scope)) {
    controllers.get(root)?.destroy();
    controllers.delete(root);
  }
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initializeWithin(document), { once: true });
} else {
  initializeWithin(document);
}
document.addEventListener("shopify:section:load", (event) => {
  if (event.target instanceof HTMLElement)
    initializeWithin(event.target);
});
document.addEventListener("shopify:section:unload", (event) => {
  if (event.target instanceof HTMLElement)
    destroyWithin(event.target);
});
